import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "react-feather";
import dayjs from "dayjs";
import ListExportToolbar from "../../stock/components/list/ListExportToolbar";
import { chequeIsPostDated, summarizeByPaymentType } from "../../dashboard/collectionsSummaryAggregator";
import { money } from "../../dashboard/dashboardHelpers";

const fmt = (iso) => (iso ? dayjs(iso).format("DD MMM YYYY") : "—");

export const isPostDatedCheque = (r) => !r.isDeleted && r.paymentMethod === 4 && chequeIsPostDated(r);

/**
 * PD (post-dated) cheque report for the Collections page (2026-10-13, "collection web page
 * not showing PD cheque report like mobile app"). Same rule as the app's supervisor list and
 * Collection Summary Report (collection_summary_aggregator.dart, ported in
 * dashboard/collectionsSummaryAggregator.js): a cheque is post-dated when its cheque date is
 * after the day it was collected.
 *
 * `rows` must be EVERY collection in the period (useCollectionsRecords), not the 100 on screen.
 * Shows the PD cheques themselves (earliest due first) and the app's Payment Type Wise
 * breakdown, which splits Cheque into Current Date / Post-Dated.
 */
const PostDatedChequesCard = ({ rows, isLoading, subtitleLines, branchCode }) => {
  const [open, setOpen] = useState(true);
  const live = useMemo(() => (rows || []).filter((r) => !r.isDeleted), [rows]);
  const pdRows = useMemo(
    () =>
      live
        .filter(isPostDatedCheque)
        .sort((a, b) => String(a.chqDate).localeCompare(String(b.chqDate))),
    [live]
  );
  const pdTotal = pdRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const byPaymentType = useMemo(() => summarizeByPaymentType(live), [live]);
  const today = dayjs().startOf("day");

  const exportColumns = [
    { header: "Cheque date", value: (r) => fmt(r.chqDate) },
    { header: "Customer", value: (r) => r.custName || (r.custId != null ? `cust ${r.custId}` : "") },
    { header: "Cheque no.", value: (r) => r.chqNumbr || "" },
    { header: "Bank", value: (r) => r.bankName || "" },
    { header: "Collected on", value: (r) => fmt(r.colectDate) },
    { header: "Collected by", value: (r) => r.empName || "" },
    { header: "Branch", value: (r) => r.branchCode || "" },
    { header: "Amount", value: (r) => Number(r.amount || 0), align: "right" },
  ];

  return (
    <div className="card mb-3">
      <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
        <button type="button" className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-1" onClick={() => setOpen((v) => !v)}>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span className="fw-semibold text-dark">Post-Dated Cheques</span>
          <span className="badge bg-warning-subtle text-warning ms-2">
            {isLoading ? "…" : `${pdRows.length} · ${money(pdTotal)}`}
          </span>
        </button>
        {open && pdRows.length > 0 && (
          <ListExportToolbar
            title="Post-Dated Cheques"
            subtitleLines={subtitleLines}
            branchCode={branchCode}
            columns={exportColumns}
            rows={pdRows}
            totals={{ Amount: pdTotal, "Cheque date": `${pdRows.length} cheque(s)` }}
            orientation="landscape"
            fileName={`PostDatedCheques_${dayjs().format("YYYYMMDD")}`}
          />
        )}
      </div>
      {open && (
        <div className="card-body">
          {isLoading ? (
            <div className="text-muted small">Loading the period&rsquo;s collections…</div>
          ) : (
            <div className="row g-3">
              <div className="col-12 col-xl-8">
                {pdRows.length === 0 ? (
                  <div className="text-muted small">No post-dated cheques in this period.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr className="text-muted" style={{ fontSize: 12 }}>
                          <th>Cheque date</th>
                          <th>Customer</th>
                          <th>Cheque no.</th>
                          <th>Bank</th>
                          <th>Collected</th>
                          <th className="text-end">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pdRows.map((r) => {
                          const due = dayjs(r.chqDate).startOf("day");
                          const days = due.diff(today, "day");
                          return (
                            <tr key={r.recordGuid}>
                              <td className="text-nowrap">
                                {fmt(r.chqDate)}
                                <div className={`small ${days <= 0 ? "text-danger fw-semibold" : days <= 7 ? "text-warning" : "text-muted"}`}>
                                  {days < 0 ? `${-days} day(s) ago` : days === 0 ? "due today" : `in ${days} day(s)`}
                                </div>
                              </td>
                              <td className="fw-semibold tx-party">{r.custName || (r.custId != null ? `cust ${r.custId}` : "—")}</td>
                              <td>{r.chqNumbr || "—"}</td>
                              <td>{r.bankName || "—"}</td>
                              <td className="small">
                                {fmt(r.colectDate)}
                                <div className="text-muted">{r.empName || ""}{r.branchCode ? ` · ${r.branchCode}` : ""}</div>
                              </td>
                              <td className="text-end fw-semibold">{money(r.amount)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={5} className="text-end text-muted small">{pdRows.length} cheque(s)</td>
                          <td className="text-end fw-bold">{money(pdTotal)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
              <div className="col-12 col-xl-4">
                <div className="text-muted small fw-semibold mb-1">Payment Type Wise Collection</div>
                <table className="table table-sm mb-0">
                  <tbody>
                    {byPaymentType.map((p) => (
                      <tr key={p.label} className={p.label.includes("Post-Dated") ? "table-warning" : ""}>
                        <td>{p.label}</td>
                        <td className="text-end text-muted small">{p.count}</td>
                        <td className="text-end fw-semibold">{money(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PostDatedChequesCard;
